import { FC, ReactNode, SyntheticEvent, useCallback, useMemo } from 'react';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { MailOutline } from '@mui/icons-material';
import { FieldValues, FormProvider, useFieldArray, UseFormReturn } from 'react-hook-form';
import {
	AddInterestToUserDto,
	ALLOWED_USER_AVATAR_MIME_TYPES,
	ALLOWED_USER_BG_IMAGE_MIME_TYPES,
	CommunityProfileOnboardingDto,
	FileUploadDto,
	fullNameToUsername,
	UpdateEventToFitnessTypeDto,
} from '@sweatpals/shared';
import { omit } from 'lodash-es';
import { borderlessInputStyles, commonFlowStyles, createCommunityFlowStyles } from './create-community-flow.styles';
import { Button, HostBackground, HostColorSelect, Photo, TextField } from '../..';
import { useAutoFocusOnMount, useCustomRegister } from '../../../hooks';
import { CreateCommunityAdditionalFormData } from '../../../models/dto';
import { PlacesAutocomplete } from '../../places-autocomplete';
import { ArrowBackIcon, AtIcon, FlashIcon, ImageIcon, PlaceIcon, TextIcon } from '../../../icons';
import { PlacesAutocompleteOption } from '../../../interfaces';
import { darkTheme } from '../../../theme';
import { Colors } from '../../../constants';
import { Option } from '../../_core';
import { clsx, GetPlacesCallback } from '../../../utils';
import { ActivityAutocomplete } from '../../activity-autocomplete/activity-autocomplete.component';

const autoFocusClass = 'js-autofocus';

export type CreateCommunityFormData = CommunityProfileOnboardingDto & CreateCommunityAdditionalFormData;

export interface CreateCommunityFlowProps<TFieldValues extends FieldValues = CreateCommunityFormData> {
	form: UseFormReturn<TFieldValues>;
	header?: ReactNode;
	rootSx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
	bodySx?: SxProps<Theme>;
	headerSx?: SxProps<Theme>;
	headerBackButtonSx?: SxProps<Theme>;
	headerContentSx?: SxProps<Theme>;
	footerSx?: SxProps<Theme>;
	footerContentSx?: SxProps<Theme>;
	interestOptions?: Option[];
	loading?: boolean;
	getPlaceOptions: GetPlacesCallback;
	onPlaceInputFocus?(event: SyntheticEvent): void;
	onBackClick?(): void;
	onSubmit?(data: TFieldValues): void | Promise<void>;
	onDiscard?(): void;
}

export const CreateCommunityFlow: FC<CreateCommunityFlowProps> = ({
	header,
	form,
	interestOptions = [],
	onSubmit,
	onDiscard,
	loading,
	onBackClick,
	rootSx,
	contentSx,
	bodySx,
	headerSx,
	headerBackButtonSx,
	headerContentSx,
	footerSx,
	footerContentSx,
	getPlaceOptions,
	onPlaceInputFocus,
}) => {
	const data = form.watch();
	const errors = form.formState.errors;

	// console.debug(data);
	// console.debug(errors);

	const communityBackgroundImageUrl = useMemo(
		() => data?.updatedBackgroundImg?.base64EncodedFile,
		[data?.updatedBackgroundImg?.base64EncodedFile]
	);

	const { register } = useCustomRegister(form);

	const submitCommunityForm = useCallback(
		async (submitData: typeof data) => {
			if (!submitData) return;

			await onSubmit?.({
				...submitData,
				interestsToInsert: submitData?.interestsToInsert?.filter((int) => !int?.delete),
			});
		},
		[onSubmit]
	);

	useAutoFocusOnMount({
		autoFocusClass,
	});

	const styles = commonFlowStyles();
	const comFlowStyles = createCommunityFlowStyles();

	const { fields: imagesToUpload, replace } = useFieldArray({
		name: 'imagesToUpload',
		control: form.control,
		keyName: 'formKey',
	});

	const imagesToUploadPlaceholder = useMemo(
		() =>
			Array.from({ length: imagesToUpload?.length < 5 ? 5 : imagesToUpload?.length + 1 }).map(
				(_, index) => imagesToUpload[index] ?? null
			),
		[imagesToUpload]
	);

	return (
		<FormProvider {...form}>
			<ThemeProvider theme={darkTheme}>
				<Box component="form" sx={clsx(styles.root, rootSx)} onSubmit={form.handleSubmit(submitCommunityForm)}>
					<HostBackground primaryColor={data?.primaryColor} background={communityBackgroundImageUrl} />

					<Box sx={clsx(styles.content, contentSx)}>
						<Box sx={clsx(styles.header, headerSx)}>
							<IconButton sx={clsx(styles.headerBackButton, headerBackButtonSx)} onClick={onBackClick}>
								<ArrowBackIcon />
							</IconButton>

							<Box sx={clsx(styles.headerContent, headerContentSx)}>{header}</Box>
						</Box>

						<Box sx={clsx(styles.body, bodySx)}>
							<Box sx={comFlowStyles.section1}>
								<Box sx={comFlowStyles.avatarBox}>
									<Photo
										{...register('updatedAvatarImg', {
											valuePropKey: 'src',
											passRef: false,
										})}
										disabled={loading}
										onChange={(value: FileUploadDto) => {
											form.setValue('updatedAvatarImg', value, {
												shouldDirty: true,
											});
										}}
										accept={ALLOWED_USER_AVATAR_MIME_TYPES.reduce((acc, key) => ({ ...acc, [key]: [] }), {})}
										circle
										width={90}
									/>
								</Box>

								<TextField
									{...register('fullName')}
									disabled={loading}
									onChange={(event) => {
										const fullName = event.target.value;
										const userName = fullNameToUsername(fullName);

										form.setValue('fullName', fullName, {
											shouldDirty: true,
										});
										form.setValue('userName', userName, {
											shouldDirty: true,
										});
									}}
									onBlur={(e) => {
										if (e.target.value) {
											form.trigger('fullName');
										}
									}}
									autoFocus
									placeholder="Community Name"
									sx={clsx(borderlessInputStyles, {
										'& .MuiOutlinedInput-input': {
											fontFamily: 'Integral CF',
											fontSize: '32px',
											fontWeight: '700',
											lineHeight: '35px',
											textAlign: 'center',
											height: 'auto',
											p: '0',

											'&::placeholder': {
												color: Colors['white_a20'],
											},
										},
									})}
									className={autoFocusClass}
								/>

								<Box display="flex" flexDirection="column" gap="16px">
									<TextField
										{...register('userName')}
										disabled={loading}
										requiredMark
										placeholder="Username"
										fullWidth
										startAdornment={
											<Box
												component={AtIcon}
												sx={{
													flexShrink: 0,
													width: '20px',
													color: Colors.white,
												}}
											/>
										}
									/>

									<TextField
										{...register('receiptsEmail')}
										disabled={loading}
										placeholder="Email"
										fullWidth
										startAdornment={
											<Box
												component={MailOutline}
												sx={{
													flexShrink: 0,
													width: '20px',
													color: Colors.white,
												}}
											/>
										}
									/>

									<PlacesAutocomplete
										values={Array.isArray(data?.addresses) ? data?.addresses : []}
										requiredMark
										disabled={loading}
										placeholder={
											data?.addresses?.length ? 'Start typing city name' : 'Where are your events based out of?'
										}
										getPlaceOptions={getPlaceOptions}
										onPlaceInputFocus={onPlaceInputFocus}
										onSelect={(value) => {
											const options = (Array.isArray(value) ? value : [value]).filter(
												Boolean
											) as PlacesAutocompleteOption[];

											form.setValue('addresses', options, {
												shouldDirty: true,
											});
										}}
										error={
											!!errors?.['addresses'] || !!errors?.['homeAddressName'] || !!errors?.['locationPrivacyOptionId']
										}
										sx={{
											'& .MuiAutocomplete-inputRoot': {
												padding: '10px 16px',

												'& .MuiInputBase-inputAdornedStart': {
													paddingLeft: '0 !important',
												},

												'& .MuiAutocomplete-endAdornment': {
													alignSelf: 'flex-start',
												},
											},
										}}
										helperText={errors?.['addresses']?.message || errors?.['homeAddressName']?.message}
										textFieldProps={{
											InputProps: {
												startAdornment: (
													<PlaceIcon
														style={{
															width: '20px',
															color: Colors.white,
														}}
													/>
												),
											},
										}}
									/>

									<TextField
										{...register('myBio')}
										disabled={loading}
										placeholder="Description of your community so people can know what you are all about!"
										fullWidth
										multiline
										minRows={2}
										maxRows={10}
										startAdornment={
											<TextIcon
												style={{
													width: '20px',
												}}
											/>
										}
									/>

									<ActivityAutocomplete
										{...register('interestsToInsert', {
											fallbackValue: [],
											passOnBlur: false,
											transformValue: (value: AddInterestToUserDto[]) =>
												value?.map(
													(int) =>
														({
															fitnessTypeId: int.interestId,
															masteryLevel: int.masteryLevel,
															delete: int.delete,
														} as UpdateEventToFitnessTypeDto)
												),
										})}
										disabled={loading}
										onChange={(values) => {
											const newValues = values?.map(
												(val) =>
													({
														interestId: val.fitnessTypeId,
														masteryLevel: val.masteryLevel,
														delete: val.delete,
													} as AddInterestToUserDto)
											);

											form.setValue('interestsToInsert', newValues, {
												shouldDirty: true,
												shouldValidate: true,
											});
										}}
										sx={{
											'&.MuiAutocomplete-inputRoot': {
												padding: '10px 16px',

												'& .MuiInputBase-inputAdornedStart': {
													paddingLeft: '0 !important',
												},

												'& .MuiAutocomplete-endAdornment': {
													alignSelf: 'flex-start',
												},
											},
										}}
										requiredMark
										placeholder="Select the activities your community offers"
										options={interestOptions}
										allowSelectMasteryLevel={false}
										startAdornment={
											<FlashIcon
												style={{
													width: '20px',
												}}
											/>
										}
									/>
								</Box>

								<Box sx={comFlowStyles.photosSection}>
									<Typography sx={comFlowStyles.photosLabel}>Community highlights:</Typography>

									<Box sx={comFlowStyles.photosBox}>
										{imagesToUploadPlaceholder?.map((file, index, array) => (
											<Photo
												value={file as FileUploadDto | null}
												key={index}
												width={112}
												height={150}
												disabled={loading}
												accept={ALLOWED_USER_BG_IMAGE_MIME_TYPES.reduce((acc, key) => ({ ...acc, [key]: [] }), {})}
												onChange={(file) => {
													const images = array.filter(Boolean) as FileUploadDto[];

													if (!file) {
														replace([...(images?.filter((_, i) => i !== index) ?? [])]);
														return;
													}

													if (!images) {
														replace([file]);

														return;
													}

													if (index < images?.length) {
														replace([...(images?.map((old, i) => (i === index ? file : old)) ?? [])]);
													} else if (index >= images?.length) {
														replace([...(images ?? []), file]);
													}
												}}
											/>
										))}
									</Box>
								</Box>
							</Box>
							<Box sx={comFlowStyles.section2}>
								<Photo
									{...register('updatedBackgroundImg', {
										passRef: false,
									})}
									onChange={(uploadFile: FileUploadDto) => {
										form.setValue('updatedBackgroundImg', uploadFile, {
											shouldDirty: true,
										});
									}}
									disabled={loading}
									accept={ALLOWED_USER_BG_IMAGE_MIME_TYPES.reduce((acc, key) => ({ ...acc, [key]: [] }), {})}
									maxWidth={389}
									sx={comFlowStyles.backgroundInput}
									wrapperSx={comFlowStyles.backgroundBox(data?.updatedBackgroundImg?.base64EncodedFile)}
									withWrapper
								>
									<Box
										sx={{
											position: 'static',

											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											gap: { xs: '17px', md: '27px' },
										}}
									>
										<Box
											component={ImageIcon}
											sx={{
												color: Colors['737373'],
												width: { xs: '20px', md: '36px' },
												height: { xs: '20px', md: '36px' },
											}}
										/>

										<Box
											sx={{
												display: 'flex',
												flexDirection: 'column',
												gap: '4px',
												textAlign: 'center',
											}}
										>
											<Typography
												sx={{
													fontFamily: 'Montserrat, sans-serif',
													fontSize: '10px',
													fontWeight: '700',
												}}
											>
												Community Cover Image
											</Typography>
											<Typography
												sx={{
													display: { xs: 'none', sm: 'block' },
													fontFamily: 'Montserrat, sans-serif',
													fontSize: '9px',
													fontWeight: '500',
													lineHeight: '1.32em',
													color: Colors.white_a80,
												}}
											>
												Drag or click anywhere in area to upload an image here
											</Typography>

											<Typography
												sx={{
													display: { xs: 'block', sm: 'none' },
													fontFamily: 'Montserrat, sans-serif',
													fontSize: '12px',
													fontWeight: '500',
													color: Colors.white_a80,
												}}
											>
												Tap anywhere in area to upload an image here
											</Typography>
										</Box>

										<Box
											sx={{
												position: 'absolute',
												bottom: '24px',
											}}
										>
											<Typography
												sx={{
													fontFamily: 'Montserrat, sans-serif',
													fontSize: '8px',
													fontWeight: '500',
													lineHeight: '21px',
													color: Colors['969696'],
													textAlign: 'center',
												}}
											>
												JPG, PNG, or SVG. Up to 4 MB.
											</Typography>
										</Box>
									</Box>
								</Photo>
								<Box sx={comFlowStyles.hostColorBox}>
									<Typography sx={comFlowStyles.hostColorLabel}>Color scheme:</Typography>

									<HostColorSelect
										{...omit(register('primaryColor', { valuePropKey: 'selectedColorId', passRef: false }), ['name'])}
										disabled={loading}
										onChange={(colorId) => {
											form.setValue('primaryColor', colorId, {
												shouldDirty: true,
											});
										}}
									/>
								</Box>
							</Box>
						</Box>

						<Box sx={clsx(styles.footer, footerSx)}>
							<Box sx={clsx(styles.footerContent, footerContentSx)}>
								{onDiscard && (
									<Button sx={styles.cancelButton} onClick={onDiscard}>
										Cancel
									</Button>
								)}
								<Button disabled={loading} sx={styles.submitButton} loading={loading} type="submit">
									Save and publish
								</Button>
							</Box>
						</Box>
					</Box>
				</Box>
			</ThemeProvider>
		</FormProvider>
	);
};
